import React, { useCallback, useEffect, useState } from "react"
import { ErrorBoundary } from "../ErrorBoundary"
import { useSyncPoll } from "../useSyncPoll"
import { SyncNowButton } from "../transactions/SyncNowButton"
import { Pagination } from "../transactions/Pagination"
import { SubscriptionSummary } from "./SubscriptionSummary"
import { SubscriptionsFilters } from "./SubscriptionsFilters"
import { SubscriptionsTable } from "./SubscriptionsTable"

const EMPTY_FILTERS = { status: "", plan: "", app_id: "", interval: "", from: "", to: "" }

// Subscriptions list (doc 07). Active + historical app subscriptions derived
// from SubscriptionCharge* events. Reads paginated, filtered data from the index
// JSON; renders summary cards + plan breakdown, filters, table, pagination.
export default function SubscriptionsApp({ subscriptionsPath, appsPath, syncPath, detailBasePath, canSync, initialAppId, copy }) {
  // Seeded from the URL app_id (per-app nav link) so the page loads scoped to
  // that app; the user can still change or clear the App filter as normal.
  const seededFilters = { ...EMPTY_FILTERS, app_id: initialAppId || "" }
  const [draft, setDraft] = useState(seededFilters)
  const [applied, setApplied] = useState(seededFilters)
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [apps, setApps] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const { syncing, startSync } = useSyncPoll(data, setReloadKey)

  useEffect(() => {
    let active = true
    fetch(appsPath, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((rows) => active && setApps(rows))
      .catch(() => {})
    return () => {
      active = false
    }
  }, [appsPath])

  const buildUrl = useCallback(() => {
    const params = new URLSearchParams()
    Object.entries(applied).forEach(([k, v]) => {
      if (v) params.set(k, v)
    })
    params.set("page", page)
    const sep = subscriptionsPath.includes("?") ? "&" : "?"
    return `${subscriptionsPath}${sep}${params}`
  }, [subscriptionsPath, applied, page])

  useEffect(() => {
    let active = true
    setLoading((prev) => prev || data === null)
    setError(false)
    fetch(buildUrl(), { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((json) => active && setData(json))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [buildUrl, reloadKey])

  const applyFilters = () => {
    setApplied(draft)
    setPage(1)
  }
  const clearFilters = () => {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    setPage(1)
  }
  const applyPreset = (from, to) => {
    const next = { ...draft, from, to }
    setDraft(next)
    setApplied(next)
    setPage(1)
  }

  const header = (
    <div className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{copy.title}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.subtitle}</p>
      </div>
      {canSync && (
        <SyncNowButton
          syncPath={syncPath}
          lastSyncedAt={data?.meta?.last_synced_at}
          syncing={syncing}
          onSyncStart={startSync}
          copy={copy}
        />
      )}
    </div>
  )

  const shell = (body) => (
    <ErrorBoundary>
      {header}
      <div className="mt-6 space-y-4">
        <SubscriptionsFilters
          draft={draft}
          setDraft={setDraft}
          onApply={applyFilters}
          onClear={clearFilters}
          onPreset={applyPreset}
          apps={apps}
          copy={copy}
        />
        {body}
      </div>
    </ErrorBoundary>
  )

  if (error) {
    return shell(
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/40 p-6">
        <p className="text-sm text-gray-700 dark:text-gray-300">{copy.states.error}</p>
      </div>
    )
  }
  if (loading || !data) {
    return shell(<p className="text-sm text-gray-400 dark:text-gray-500">{copy.states.loading}</p>)
  }
  if (data.total === 0) {
    return shell(
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-10 text-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{copy.empty.title}</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{copy.empty.body}</p>
      </div>
    )
  }

  return shell(
    <>
      <SubscriptionSummary summary={data.summary} copy={copy} />
      <SubscriptionsTable rows={data.rows} copy={copy} detailBasePath={detailBasePath} />
      <Pagination
        page={data.page}
        perPage={data.per_page}
        total={data.total}
        onPage={setPage}
        copy={copy}
      />
    </>
  )
}
